import { NgClass, NgStyle } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { TooltipPosition } from '../../models/types/tooltip-positions.types';

@Component({
  selector: 'lib-tooltip',
  standalone: true,
  imports: [NgClass, NgStyle],
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.css'],
})
export class TooltipComponent implements OnChanges {
  @Input() text = '';
  @Input() position: TooltipPosition = 'top';
  @Input() tooltipClass? = '';
  @Input() arrowColorClass = 'border-neutral-lighter';
  @Input() useFixed = false;
  // For tooltips: alignment of the tooltip relative to the host element.
  @Input() align: 'center' | 'end' = 'center';

  public tooltipBoxClass = '';
  public triangleArrowClass = '';
  public isOpen = false;
  public fixedStyles: Record<string, string> = {};

  private static openTooltip: TooltipComponent | null = null;

  constructor(private el: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['position']) {
      this.setTooltipClasses(this.position);
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.recalcFixedPosition();
  }

  @ViewChild('tooltipContent', { static: true }) tooltipContent!: ElementRef;
  private recalcFixedPosition(): void {
    const tootipElementRect = (this.el.nativeElement as HTMLElement).getBoundingClientRect();
    const tooltipContent = this.tooltipContent.nativeElement as HTMLElement;
    const tooltipContentRect = tooltipContent.getBoundingClientRect();

    const styles = window.getComputedStyle(tooltipContent);
    const marginTop = parseFloat(styles.marginTop);
    const marginBottom = parseFloat(styles.marginBottom);

    const tooltipHeight = tooltipContentRect.height + marginTop + marginBottom;
    const wouldOverflowTop = tootipElementRect.top - tooltipHeight < 0;

    let position = this.position;
    if (wouldOverflowTop) {
      position = 'bottom';
    }
    this.setTooltipClasses(position as TooltipPosition);

    // return if we are setting useFixed
    if (!this.useFixed) return;

    // Position to the right of the host element, vertically centred.
    this.fixedStyles = {
      top: `${tootipElementRect.top + tootipElementRect.height / 2}px`,
      left: `${tootipElementRect.right + 8}px`, // 8 px = ~ml-2
      transform: 'translateY(-50%)',
    };
  }

  private setTooltipClasses(position: TooltipPosition): void {
    const color = this.arrowColorClass;
    const [boxX, arrowX] = this.align === 'end'
      ? ['right-0', 'right-2']
      : ['left-1/2 -translate-x-1/2', 'left-1/2 -translate-x-1/2'];
    switch (position) {
      case 'top':
        this.tooltipBoxClass = `bottom-full mb-2 ${boxX}`;
        this.triangleArrowClass = `top-full ${arrowX} border-x-6 border-x-transparent border-t-6 ${color}`;
        break;
      case 'bottom':
        this.tooltipBoxClass = `top-full mt-2 ${boxX}`;
        this.triangleArrowClass = `bottom-full ${arrowX} border-x-6 border-x-transparent border-b-6 ${color}`;
        break;
      case 'left':
        this.tooltipBoxClass = 'right-full mr-2 top-1/2 -translate-y-1/2';
        this.triangleArrowClass = `left-full top-1/2 -translate-y-1/2 border-y-6 border-y-transparent border-l-6 ${color}`;
        break;
      case 'right':
        this.tooltipBoxClass = 'left-full ml-2 top-1/2 -translate-y-1/2';
        this.triangleArrowClass = `right-full top-1/2 -translate-y-1/2 border-y-6 border-y-transparent border-r-6 ${color}`;
        break;
      default:
        this.tooltipBoxClass = '';
        this.triangleArrowClass = '';
    }
  }

  toggleTooltip(event: Event) {
    event.stopPropagation();

    const isTouchDevice = !window.matchMedia(
      '(hover: hover) and (pointer: fine)'
    ).matches;
    if (!isTouchDevice) return;

    if (TooltipComponent.openTooltip && TooltipComponent.openTooltip !== this) {
      TooltipComponent.openTooltip.isOpen = false;
    }

    this.isOpen = !this.isOpen;

    TooltipComponent.openTooltip = this.isOpen ? this : null;
  }

  @HostListener('document:click', ['$event'])
  closeTooltipOnOutsideClick(event: Event) {
    const target = event.target as HTMLElement;

    if (!this.el.nativeElement.contains(target)) {
      this.isOpen = false;

      if (TooltipComponent.openTooltip === this) {
        TooltipComponent.openTooltip = null;
      }
    }
  }
}
